import { deleteAttachmentById, getAttachmentById, getPageState } from '../DBService/DbService';
import { isIosDevice, isMobileDevice } from './NetworkConnectionService';
import { checkPermissionMicrophone, checkWebPermission, requestPermissionMicrophone } from './PermissionsService';

export class AudioService {
    private static instance: AudioService;
    private recorder: any;
    private chunks: any[];
    private possibleTypes: string[];

    public static getInstance(): AudioService {
        if (!AudioService.instance) {
            AudioService.instance = new AudioService();
        }
        return AudioService.instance;
    }

    constructor() {
        this.recorder = null;
        this.chunks = [];
        this.possibleTypes = [
            'audio/mp4',    // Preferred for iOS
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/ogg;codecs=opus',
            'audio/ogg'
        ];
    }

    public async attemptStartRecording(onAfterSuccess: (blob: Blob, url: string) => void): Promise<any> {
        // if recorder exists and it's not currently recording, kill it
        if (this.recorder) {
            if (this.recorder.state === 'recording') {
                console.log('Browser recorder is currently recording, not killing...');
                return; // don't start a new recording if one is already active
            }

            this.killRecorder();
        }

        const onError = (err: any) => {
            console.error('onError in attemptStartRecording:', err);
        };

        const onStatusChange = (status: { isRecording: boolean }) => {
            console.log('onStatusChange:', status);
        };

        try {
            const recorderResult = await this.startRecording(onAfterSuccess, onError, onStatusChange);
            this.recorder = recorderResult;
            return recorderResult;
        } catch (error) {
            console.error('startRecording failed:', error);
        }
    }

    getSupportedMimeType = (): string | undefined => {
        for (const type of this.possibleTypes) {
            if (MediaRecorder.isTypeSupported(type)) {
                return type;
            }
        }

        return undefined;
    };

    getSupportedFileExtension = (): string | undefined => {
        const mimeToExt: Record<string, string> = {
            'audio/mp4': 'mp4',
            'audio/webm;codecs=opus': 'webm',
            'audio/webm': 'webm',
            'audio/ogg;codecs=opus': 'ogg',
            'audio/ogg': 'ogg'
        };

        const possibleTypes = Object.keys(mimeToExt);

        for (const type of possibleTypes) {
            if (MediaRecorder.isTypeSupported(type)) {
                return mimeToExt[type];
            }
        }

        return undefined;
    };

    isSuportedMimeType = (mime: string): boolean => {
        return this.possibleTypes.includes(mime);
    }

    saveToFileSystem = async (blob: Blob, filename: string): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const data = reader.result as ArrayBuffer;
                const dataDir = (window.cordova as any).file.dataDirectory; // App sandbox directory

                window.resolveLocalFileSystemURL(
                    dataDir,
                    (dirEntry: any) => {
                        dirEntry.getFile(
                            filename,
                            { create: true, exclusive: false },
                            (fileEntry: any) => {
                                fileEntry.createWriter((fileWriter: any) => {
                                    fileWriter.onwriteend = () => {
                                        resolve(fileEntry.toURL()); // Return file URL
                                    };
                                    fileWriter.onerror = (e: any) => reject(e);
                                    fileWriter.write(new Blob([data], { type: blob.type }));
                                });
                            },
                            reject
                        );
                    },
                    reject
                );
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(blob);
        });
    };

    async startRecording(
        onSuccess: (blob: Blob, url: string) => void,
        onError: (err: any) => void,
        onStatusChange: (status: { isRecording: boolean }) => void
    ): Promise<any> {
        try {
            if (isMobileDevice()) {
                const hasPermission = await checkPermissionMicrophone();
                if (!hasPermission) {
                    const granted = await requestPermissionMicrophone();
                    if (!granted) {
                        onError(new Error('Microphone permission not granted'));
                        return null;
                    }
                }
            } else {
                try {
                    await checkWebPermission('microphone');
                } catch (error) {
                    onError(new Error('Microphone permission not granted'));
                    return null;
                }
            }

            const stream: MediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = this.getSupportedMimeType();

            if (!mimeType) {
                throw new Error('No supported audio MIME type found for MediaRecorder');
            }

            const mediaRecorder = new MediaRecorder(stream, { mimeType });

            mediaRecorder.ondataavailable = async (event: any) => {
                if (event.data && event.data.size > 0) {
                    this.chunks.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                try {
                    const blob = new Blob(this.chunks, { type: mimeType });
                    stream.getTracks() // get all tracks from the MediaStream
                        .forEach(track => track.stop()); // stop each of them
                    const fileUrl = await this.generateAudioUrl(blob);
                    if (fileUrl) {
                        onSuccess(blob, fileUrl);
                    } else {
                        throw new Error('Something went wrong with generating audio url.');
                    }
                } catch (error) {
                    console.error('[AudioService] startRecording - Error saving file:', error);
                }
            };

            mediaRecorder.start();
            onStatusChange({ isRecording: true });

            return mediaRecorder;
        } catch (err) {
            onError(err);
            return null;
        }
    }

    generateAudioUrl = async (blob: Blob): Promise<string | undefined> => {
        if (isIosDevice()) {
            const fileUrl = await this.saveToFileSystem(blob, `recording_${Date.now()}.mp4`);
            console.log('[AudioService] generateAudioUrl: ', fileUrl);
            return fileUrl;
        } else {
            return new Promise<string | undefined>((resolve, reject) => {
                const reader = new FileReader();

                reader.onload = () => {
                    resolve(reader.result as string);
                };

                reader.onerror = (err) => {
                    console.error('[AudioService] generateAudioUrl error: ', err);
                    resolve(undefined);
                };

                reader.readAsDataURL(blob);
            });
        }
    }

    public attemptStopRecording(): void {
        this.stopRecording();
    }

    stopRecording() {
        if (!this.recorder) {
            console.error('Recorder is null. Skipping stop.');
            return;
        }

        if (this.recorder.state !== 'inactive') {
            this.recorder.stop();
        }
    }

    private killRecorder(): void {
        if (!!this.recorder) {
            if (this.recorder.release) {
                this.recorder.release();
            }
            delete this.recorder;
            this.recorder = null;
        }
    }

    async getAttachedAudioRecordingAudioUrl(
        dbname: string | undefined,
        elementId: string,
        iosLocalFilename?: string
    ): Promise<string | null> {
        try {
            if (!dbname) {
                return null;
            }

            const doc = await getPageState(dbname);
            const extension = this.getSupportedFileExtension();
            const attachmentId = `${elementId}.${extension}`;
            console.log('[getAttachedAudioRecordingAudioUrl] doc: ', doc);
            console.log('[getAttachedAudioRecordingAudioUrl] extension: ', extension);
            console.log('[getAttachedAudioRecordingAudioUrl] attachmentId: ', attachmentId);
            console.log('[getAttachedAudioRecordingAudioUrl] iosLocalFilename: ', iosLocalFilename);

            if (doc?._attachments && doc._attachments[attachmentId]) {
                const blob = await getAttachmentById(dbname, attachmentId) as Blob;

                if (isIosDevice()) {
                    if (!iosLocalFilename) {
                        throw new Error('iOS local filename required but not provided');
                    }
                    return await this.getAttachedAudioRecordingAudioUrl_iOS(iosLocalFilename, blob);
                }

                // Android/web uses data URL
                return await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            }

            console.error("[AudioService] No attachment found for:", attachmentId);
            return null;
        } catch (error) {
            console.error(error);
            return null;
        }
    }


    async getAttachedAudioRecordingAudioUrl_iOS(
        fileName: string,
        blob: Blob
    ): Promise<string> {
        return new Promise((resolve, reject) => {
            const targetPath = (window.cordova as any).file.dataDirectory + fileName;
            console.log('[AudioService] getAttachedAudioRecordingAudioUrl_iOS targetPath: ', targetPath);

            window.resolveLocalFileSystemURL(
                targetPath,
                () => {
                    resolve(targetPath);
                },
                () => {
                    // file missing, so restore from db blob
                    window.resolveLocalFileSystemURL(
                        (window.cordova as any).file.dataDirectory,
                        (dirEntry: any) => {
                            dirEntry.getFile(
                                fileName,
                                { create: true },
                                (fileEntry: any) => {
                                    fileEntry.createWriter((fileWriter: any) => {
                                        fileWriter.onwriteend = () => resolve(targetPath);
                                        fileWriter.onerror = reject;
                                        fileWriter.write(blob);
                                    });
                                },
                                reject
                            );
                        },
                        reject
                    );
                }
            );
        });
    }

    async deleteAttachedAudioRecording(docId: string, elementId: string) {
        try {
            const db = await getPageState(docId);
            const extension = this.getSupportedFileExtension();
            const attachmentId = `${elementId}.${extension}`;
            console.log('[AudioService] deleteAttachedAudioRecording attachmentId: ', attachmentId);
            if (db?._attachments && db._attachments[attachmentId]) {
                await deleteAttachmentById(docId, attachmentId, (db as any)._rev);
            } else {
                console.error('[AudioService] deleteAttachedAudioRecording: document has no attachments or attachment not found');
            }
        } catch (error) {
            console.error(error);
        }
    }

    getAudioFileName(userCode: string | undefined, moduleId: string): string {
        const now = new Date();
        const day = now.getDate().toString().padStart(2, '0');
        const month = (now.getMonth() + 1).toString().padStart(2, '0'); // months are zero-indexed
        const year = now.getFullYear();
        return `${userCode || 'no-user-code'}_${moduleId}_${day}-${month}-${year}`;
    }

    // async archivePastAudioRecordings(): Promise<void> {
    //     try {
    //         const pastApplicableModules = await getPastApplicableModules();
    //         const pastApplicableModulesIds = pastApplicableModules.map(module => module._id);
    //         // console.log('pastApplicableModulesIds: ', pastApplicableModulesIds);
    //         const userdocs = await getUserdataDocs();
    //         const userdocsOfPastModules = userdocs.filter(userdoc => {
    //             return pastApplicableModulesIds.some(moduleId => userdoc._id.includes(moduleId));
    //         });
    //         // console.log('userdocsOfPastModules: ', userdocsOfPastModules);

    //         let totalArchived = 0;
    //         for (const doc of userdocsOfPastModules) {
    //             const updated = await archiveAudioWebmAttachmentsInDoc(doc._id);
    //             if (updated) {
    //                 totalArchived++;
    //             }
    //         }

    //         console.log(`Archived ${totalArchived} documents.`);
    //     } catch (error) {
    //         console.error('Something went wrong with archiving past audio recordings: ', error);
    //     }
    // }

    // private async save(audioRecordingBlob: Blob): Promise<void> {
    //     console.log('save...');
    //     const audioRecordingContent = await this.blobToBase64(audioRecordingBlob);
    //     // save it to local file system
    //     // TODO: save audio path in db
    //     this.saveBase64ToFile(audioRecordingContent, 'recording.ogg');
    // }

    // private blobToBase64(blob: Blob): Promise<string> {
    //     return new Promise((resolve, reject) => {
    //         const reader = new FileReader();
    //         reader.onloadend = (event) => {
    //             const base64data = reader.result || event.target?.result;
    //             resolve(base64data as string);
    //         };
    //         reader.onerror = (err) => {
    //             console.log('reading file: ', JSON.stringify(err, null, 2));
    //             reject(err);
    //         };
    //         reader.readAsDataURL(blob);
    //     });
    // }

    // saveBase64ToFile = (
    //     base64Data: string,
    //     filename: string = 'recording.ogg',
    //     contentType: string = 'audio/ogg'
    // ): void => {
    //     // Convert base64 to a Blob first
    //     const byteCharacters = atob(base64Data.split(',')[1] || base64Data);
    //     const byteNumbers = new Array(byteCharacters.length);
    //     for (let i = 0; i < byteCharacters.length; i++) {
    //         byteNumbers[i] = byteCharacters.charCodeAt(i);
    //     }
    //     const byteArray = new Uint8Array(byteNumbers);
    //     const blob = new Blob([byteArray], { type: contentType });

    //     (window as any).resolveLocalFileSystemURL(
    //         (window.cordova as any).file.dataDirectory, // use cordova.file.externalDataDirectory for shared/public files
    //         (directoryEntry: FileSystemDirectoryEntry) => {
    //             directoryEntry.getFile(
    //                 filename,
    //                 { create: true, exclusive: false },
    //                 (fileEntry: any) => {
    //                     fileEntry.createWriter((fileWriter: any) => {
    //                         fileWriter.onwriteend = () => {
    //                             console.log('File successfully written at: ', fileEntry.toURL());
    //                             console.log('Actual file path:', fileEntry.nativeURL);

    //                             // TODO THIS IS TEMPORARY, UNTIL IT'S SAVED IN DB:
    //                             // copy it to Downloads
    //                             const downloadsDir = (window.cordova as any).file.externalRootDirectory + 'Download/';
    //                             (window as any).resolveLocalFileSystemURL(downloadsDir, (downloadsDirEntry: any) => {
    //                                 fileEntry.copyTo(downloadsDirEntry, filename, (copiedEntry: any) => {
    //                                     console.log('Copied to Downloads:', copiedEntry.fullPath);
    //                                 }, (err: any) => {
    //                                     console.error('Copy to Downloads failed', err);
    //                                 });
    //                             }, (err: any) => {
    //                                 console.error('Could not resolve Downloads directory', err);
    //                             });
    //                         };

    //                         fileWriter.onerror = (e: any) => {
    //                             console.error('Failed to write file: ', e);
    //                         };

    //                         fileWriter.write(blob);
    //                     }, console.error);
    //                 },
    //                 console.error
    //             );
    //         },
    //         console.error
    //     );
    // }
}